import React from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import { Menu as ReakitMenu } from 'reakit';
import styled from 'styled-components';
import { borders, boxShadows, colors, spaces } from '../../settings';
import { MenuButton } from './MenuButton';
import { MenuDivider } from './MenuDivider';
import { MenuItem } from './MenuItem';
import { useMenu } from './useMenu';

export interface MenuProps {
    children: React.ReactNode;
    disabled?: boolean;
    disclosure: React.ReactElement;
    isOpen?: boolean;
}

const StyledReakitMenu = styled(ReakitMenu).attrs<{ className?: string }>({ className: 'emu' })`
    outline: none;
    z-index: 99; /* Same as tooltip */
`;

export const InnerMenu = styled.div`
    background-color: ${colors.white};
    border-radius: ${borders.borderRadiuses.medium}px;
    box-shadow: ${boxShadows.low};
    max-height: 50vh;
    min-width: 250px;
    opacity: 0;
    overflow-y: auto;
    padding: ${spaces['0.75bu']} 0;
    transition: opacity 0.2s;

    [data-enter] & {
        opacity: 1;
    }
`;

/**
 * Renders a list of options (buttons or links) inside of a small dialog.
 * The disclosure element should accept event listeners and aria-properties for accessibility.
 * Valid children are MenuItem and MenuDivider.
 * Follows the WAI-ARIA Menu pattern, with an WAI-ARIA Menu Button pattern used for the disclosure.
 * Note that this component isn't a replacement for all dialogs, nor should be used for `nav` like structures.
 * For creating more bespoke components, @see useMenu.
 */
export const Menu = React.forwardRef<HTMLDivElement, MenuProps>(
    ({ children, disabled, disclosure, isOpen }, ref) => {
        const menuProps = useMenu({ visible: isOpen });
        const disclosureId = `${menuProps.baseId}-disclosure`;
        const items = flattenChildren(children);
        if (
            !items.every(
                (item) =>
                    typeof item === 'object' &&
                    (item.type === MenuItem || item.type === MenuDivider)
            )
        ) {
            console.error(
                'All child nodes within a menu component must be a MenuItem or MenuDivider'
            );
        }

        return (
            <>
                <MenuButton
                    ref={ref}
                    id={disclosureId}
                    {...menuProps}
                    {...disclosure.props}
                    disabled={disabled}
                >
                    {disclosure}
                </MenuButton>
                <StyledReakitMenu aria-labelledby={disclosureId} disabled={disabled} {...menuProps}>
                    <InnerMenu>
                        {React.Children.map(items, (child) => {
                            if (React.isValidElement(child)) {
                                return React.cloneElement(child, menuProps);
                            }

                            return;
                        })}
                    </InnerMenu>
                </StyledReakitMenu>
            </>
        );
    }
);

/**
 * Re-export BaseMenu to help create custom Menu implementations
 * @see useMenu
 */
export { ReakitMenu as BaseMenu };
